import { useState, useEffect, useRef } from "react";
import { TrendingUp, Minus, Star, Shield, Target, Zap, BarChart2, XCircle, CheckCircle, Flame, HelpCircle, X, Layers, TrendingDown, ChevronDown } from "lucide-react";
import BetTag from "./BetTag";
import BetTagsLegend from "./BetTagsLegend";
import Counter from "./Counter";
import { springFill, auraPulse } from "@/lib/motion";
import PremiumGate from "./PremiumGate";

// Container for the Target Kitano panel — wraps the existing visuals and
// applies a perpetually breathing neon aura via anime.js v4. The aura
// turns green when the pick is flagged as a value-bet, amber otherwise.
function KitanoCard({ accent = "default", children }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const isValue = accent === "value";
    const inst = auraPulse(ref.current, {
      color: isValue ? "rgba(34,197,94,0.55)" : "rgba(245,158,11,0.55)",
      offColor: isValue ? "rgba(34,197,94,0)" : "rgba(245,158,11,0)",
      intensity: 36, duration: 2400, scaleAmp: 1.005,
    });
    return () => { inst?.cancel?.(); };
  }, [accent]);
  return (
    <div
      ref={ref}
      className="neon-amber scanlines relative overflow-hidden target-kitano-panel"
      style={{
        background: "linear-gradient(135deg, #0e0900 0%, #171000 60%, #0e0a00 100%)",
        border: accent === "value" ? "2px solid #22C55E" : "2px solid #F59E0B",
        willChange: "box-shadow, transform",
      }}
      data-testid="target-kitano">
      {children}
    </div>
  );
}

/* ─── Neon keyframe styles — injected once ─────────────────────────────── */
const NEON_STYLES = `
  @keyframes neon-amber {
    0%,100% { box-shadow: 0 0 18px rgba(245,158,11,0.45), 0 0 45px rgba(245,158,11,0.18), 0 0 80px rgba(245,158,11,0.07); }
    50%      { box-shadow: 0 0 32px rgba(245,158,11,0.70), 0 0 70px rgba(245,158,11,0.30), 0 0 120px rgba(245,158,11,0.12); }
  }
  @keyframes neon-purple {
    0%,100% { box-shadow: 0 0 18px rgba(167,139,250,0.40), 0 0 45px rgba(167,139,250,0.15), 0 0 80px rgba(167,139,250,0.06); }
    50%      { box-shadow: 0 0 32px rgba(167,139,250,0.65), 0 0 70px rgba(167,139,250,0.28), 0 0 120px rgba(167,139,250,0.10); }
  }
  @keyframes neon-cyan {
    0%,100% { box-shadow: 0 0 18px rgba(6,182,212,0.40), 0 0 45px rgba(6,182,212,0.15), 0 0 80px rgba(6,182,212,0.06); }
    50%      { box-shadow: 0 0 32px rgba(6,182,212,0.65), 0 0 70px rgba(6,182,212,0.28), 0 0 120px rgba(6,182,212,0.10); }
  }
  @keyframes bar-glow-green {
    0%,100% { opacity: 0.82; filter: brightness(1) drop-shadow(0 0 3px rgba(34,197,94,0.5)); }
    50%      { opacity: 1;    filter: brightness(1.15) drop-shadow(0 0 8px rgba(34,197,94,0.85)); }
  }
  @keyframes bar-glow-blue {
    0%,100% { opacity: 0.82; filter: brightness(1) drop-shadow(0 0 3px rgba(59,130,246,0.5)); }
    50%      { opacity: 1;    filter: brightness(1.15) drop-shadow(0 0 8px rgba(59,130,246,0.85)); }
  }
  @keyframes bar-glow-cyan {
    0%,100% { opacity: 0.82; filter: brightness(1) drop-shadow(0 0 3px rgba(6,182,212,0.5)); }
    50%      { opacity: 1;    filter: brightness(1.15) drop-shadow(0 0 8px rgba(6,182,212,0.85)); }
  }
  @keyframes bar-glow-red {
    0%,100% { opacity: 0.82; filter: brightness(1) drop-shadow(0 0 3px rgba(239,68,68,0.5)); }
    50%      { opacity: 1;    filter: brightness(1.15) drop-shadow(0 0 8px rgba(239,68,68,0.85)); }
  }
  @keyframes bar-glow-amber {
    0%,100% { opacity: 0.82; filter: brightness(1) drop-shadow(0 0 3px rgba(245,158,11,0.5)); }
    50%      { opacity: 1;    filter: brightness(1.15) drop-shadow(0 0 8px rgba(245,158,11,0.85)); }
  }
  .neon-amber  { animation: neon-amber  2.8s ease-in-out infinite; }
  .neon-purple { animation: neon-purple 2.8s ease-in-out infinite; }
  .neon-cyan   { animation: neon-cyan   2.8s ease-in-out infinite 0.9s; }
  .prob-bar    { animation: bar-glow-green 2.6s ease-in-out infinite; }
  .prob-bar[style*="#22C55E"]  { animation: bar-glow-green 2.6s ease-in-out infinite; }
  .prob-bar[style*="#3B82F6"]  { animation: bar-glow-blue  2.6s ease-in-out infinite 0.4s; }
  .prob-bar[style*="#06B6D4"]  { animation: bar-glow-cyan  2.6s ease-in-out infinite 0.8s; }
  .prob-bar[style*="#EF4444"]  { animation: bar-glow-red   2.6s ease-in-out infinite 1.2s; }
  .prob-bar[style*="#F59E0B"]  { animation: bar-glow-amber 2.6s ease-in-out infinite 1.6s; }
  .scanlines::before {
    content:''; position:absolute; inset:0; pointer-events:none; z-index:0;
    background: repeating-linear-gradient(0deg, rgba(255,255,255,0.015) 0px, rgba(255,255,255,0.015) 1px, transparent 1px, transparent 3px);
  }
`;

const C = { surface: "var(--bc-panel, #18181B)", highlight: "var(--bc-panel-elev, #27272A)", border: "var(--bc-border, #27272A)", text: "var(--bc-ink, #FAFAFA)", muted: "var(--bc-sub, #A1A1AA)", green: "#22C55E", blue: "#3B82F6", red: "#EF4444", yellow: "#F59E0B", cyan: "#06B6D4" };

function ProbBar({ label, prob, color, sublabel }) {
  const barRef = useRef(null);
  useEffect(() => {
    if (!barRef.current) return;
    springFill(barRef.current, prob, { stiffness: 95, damping: 11 });
  }, [prob]);
  // Cursor-aware ripple: write hover X-position to a CSS var on the bar
  const onMove = (e) => {
    if (!barRef.current) return;
    const r = barRef.current.getBoundingClientRect();
    barRef.current.style.setProperty("--bar-mx", `${e.clientX - r.left}px`);
  };
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-baseline">
        <span className="font-heading font-bold text-xs uppercase tracking-wider" style={{ color: C.muted }}>{label}</span>
        <Counter
          value={prob}
          decimals={0}
          suffix="%"
          duration={900}
          kachunk
          glowColor={`${color}cc`}
          className="font-data font-medium"
          style={{ color, fontSize: "1.5rem", lineHeight: 1, textShadow: `0 0 12px ${color}80` }}
        />
      </div>
      {sublabel && <p className="font-data text-xs" style={{ color: C.muted }}>{sublabel}</p>}
      <div
        ref={barRef}
        onMouseMove={onMove}
        className="bc-live-bar h-2.5 overflow-hidden relative"
        style={{ background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))", borderRadius: 4, border: "1px solid var(--bc-border, rgba(255,255,255,0.07))" }}
      >
        <div
          className="bc-fill h-full prob-bar relative overflow-hidden"
          style={{
            width: `0%`,
            background: `linear-gradient(90deg, ${color}35, ${color}80, ${color}, ${color}80, ${color}35)`,
            boxShadow: `0 0 14px ${color}70, 0 0 28px ${color}30`,
            borderRadius: "3px 0 0 3px",
          }}
        >
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "42%",
            background: "linear-gradient(to bottom, rgba(255,255,255,0.22), transparent)" }} />
        </div>
        <div className="bc-shine" style={{
          position: "absolute", top: 0, left: 0, height: "100%", width: "30%",
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
          opacity: 0, pointerEvents: "none",
        }} />
      </div>
    </div>
  );
}

function GoalRow({ label, prob, color }) {
  const barRef = useRef(null);
  useEffect(() => {
    if (!barRef.current) return;
    springFill(barRef.current, prob, { stiffness: 100, damping: 12 });
  }, [prob]);
  const onMove = (e) => {
    if (!barRef.current) return;
    const r = barRef.current.getBoundingClientRect();
    barRef.current.style.setProperty("--bar-mx", `${e.clientX - r.left}px`);
  };
  return (
    <div className="flex items-center gap-3 py-2" style={{ borderBottom: `1px solid ${C.highlight}` }}>
      <span className="font-heading font-bold text-xs uppercase tracking-wider w-28 flex-shrink-0" style={{ color: C.muted }}>{label}</span>
      <div
        ref={barRef}
        onMouseMove={onMove}
        className="bc-live-bar flex-1 h-2.5 overflow-hidden relative"
        style={{ background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))", borderRadius: 3, border: "1px solid var(--bc-border, rgba(255,255,255,0.07))" }}
      >
        <div
          className="bc-fill h-full prob-bar relative overflow-hidden"
          style={{
            width: `0%`,
            background: `linear-gradient(90deg, ${color}35, ${color}80, ${color}, ${color}80, ${color}35)`,
            boxShadow: `0 0 10px ${color}60`,
            borderRadius: "2px 0 0 2px",
          }}
        >
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "42%",
            background: "linear-gradient(to bottom, rgba(255,255,255,0.2), transparent)" }} />
        </div>
        <div className="bc-shine" style={{
          position: "absolute", top: 0, left: 0, height: "100%", width: "30%",
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)",
          opacity: 0, pointerEvents: "none",
        }} />
      </div>
      <Counter
        value={prob}
        decimals={0}
        suffix="%"
        duration={900}
        className="font-data text-sm font-medium w-12 text-right"
        style={{ color: C.text }}
      />
    </div>
  );
}

function MulrigolRow({ label, prob, highlight }) {
  const color = prob > 60 ? C.green : prob > 45 ? C.yellow : C.blue;
  return (
    <div className="flex items-center gap-3 py-1.5" style={{ borderBottom: `1px solid ${C.highlight}` }}>
      <span className="font-heading font-bold text-xs uppercase w-28 flex-shrink-0" style={{ color: highlight ? color : C.muted }}>{label}</span>
      <div
        className="flex-1 h-2 overflow-hidden relative"
        style={{ background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))", borderRadius: 3, border: "1px solid var(--bc-border, rgba(255,255,255,0.07))" }}
      >
        <div
          className="h-full prob-bar relative overflow-hidden"
          style={{
            width: `${prob}%`,
            background: highlight
              ? `linear-gradient(to right, ${color}35, ${color}80, ${color})`
              : `linear-gradient(to right, ${color}18, ${color}45)`,
            boxShadow: highlight ? `0 0 8px ${color}60` : "none",
            borderRadius: "2px 0 0 2px",
          }}
        >
          {highlight && (
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "42%",
              background: "linear-gradient(to bottom, rgba(255,255,255,0.2), transparent)" }} />
          )}
        </div>
      </div>
      <span className="font-data text-xs font-medium w-10 text-right" style={{ color }}>{prob}%</span>
      {highlight && <span className="font-data text-xs px-1" style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}>★</span>}
    </div>
  );
}

const BET_ICON = { "1x2": TrendingUp, "doppia": Shield, "gol": Target, "multigol": BarChart2, "none": Minus };

// BetBtn defined outside PredictionPanel to avoid recreation on every render
function BetBtn({ bet, prob, type, isSelected, onAddBet }) {
  const sel = isSelected(bet);
  const btnRef = useRef(null);
  return (
    <button
      ref={btnRef}
      data-testid={`bet-btn-${bet.replace(/\s/g, '-')}`}
      onClick={(e) => {
        if (!sel) {
          // Coin-fly satisfaction: spawn a 🪙 from this button toward the
          // multipla counter (data-testid="multipla-counter") in the navbar.
          // Falls through silently if the target isn't on screen.
          import("@/lib/motion").then(({ flyToTarget }) => {
            const target = document.querySelector("[data-testid='nav-miei']")
              || document.querySelector("[data-testid='multipla-counter']");
            if (target && btnRef.current) flyToTarget(btnRef.current, target);
          });
        }
        onAddBet && onAddBet({ bet, prob, type });
      }}
      className="flex items-center gap-1 px-2 py-1 transition-all text-xs font-heading uppercase font-bold"
      style={{ background: sel ? "rgba(59,130,246,0.15)" : "var(--bc-panel-elev, rgba(255,255,255,0.03))",
        border: sel ? "1.5px solid #3B82F6" : "1px solid #27272A",
        color: sel ? "#3B82F6" : "#A1A1AA" }}>
      {sel ? <span style={{ color: "#3B82F6" }}>&#10003;</span> : <span style={{ color: "var(--bc-sub, #A1A1AA)" }}>+</span>}
      <span className="truncate" style={{ maxWidth: 120 }}>{bet}</span>
      <span className="font-data" style={{ color: sel ? "#3B82F6" : prob > 60 ? "#22C55E" : "#A1A1AA" }}>{prob}%</span>
    </button>
  );
}

export default function PredictionPanel({ predictions, homeTeam, awayTeam, onAddBet, selectedBets = [], isPremium = true }) {
  const { combined, risk, recommended_bets, confidence, multigol, target_kitano, hyper_combo, affidabilita, value } = predictions;
  // Show ValuebetStrip ONLY for picks already surfaced as "Scommesse
  // consigliate" with has_value=true. Enrich each row with the
  // implied_prob / source from value_data so we can display the same
  // "Modello X% · Pinnacle (de-vig) Y%" line as before.
  const labelToValueKey = {
    "1 (Vittoria Casa)": "home", "X (Pareggio)": "draw", "2 (Vittoria Ospite)": "away",
    "Over 1.5": "over15", "Under 1.5": "under15", "Over 2.5": "over25", "Under 2.5": "under25",
    "Over 3.5": "over35", "Under 3.5": "under35",
    "Gol/Gol (BTTS)": "btts_yes", "No Gol/Gol": "btts_no",
    "1X (Casa o Pareggio)": "double_1x", "X2 (Ospite o Pareggio)": "double_x2", "12 (Casa o Ospite)": "double_12",
    "Multigol 0-1": "mg_0_1", "Multigol 1-2": "mg_1_2", "Multigol 1-3": "mg_1_3",
    "Multigol 2-3": "mg_2_3", "Multigol 2-4": "mg_2_4",
  };
  const valueBets = (recommended_bets || [])
    .filter((b) => b && b.has_value && b.bet)
    .map((b) => {
      const key = labelToValueKey[b.bet] || b.bet;
      const v = (value && value[labelToValueKey[b.bet]]) || {};
      return [key, {
        _label: b.bet,
        _type: b.type || "value",
        model_prob: b.prob ?? v.model_prob,
        implied_prob: v.implied_prob,
        odds: b.book_odds ?? v.odds,
        value_pct: b.value_pct ?? v.value_pct,
        source: v.source || "market",
      }];
    })
    .sort((a, b) => (b[1].value_pct || 0) - (a[1].value_pct || 0));
  const dominant = combined.home > combined.away ? "home" : (combined.away > combined.home ? "away" : "draw");
  const tk = target_kitano || {};
  const hc = hyper_combo || {};
  const aff = affidabilita || {};
  const isSelected = (betName) => selectedBets.some((b) => b.bet === betName);
  const [showAffHelp, setShowAffHelp] = useState(false);

  // Best multigol ranges for highlight
  const mgRanges = multigol ? [
    { label: "Multigol 0-1", key: "mg_0_1" }, { label: "Multigol 1-2", key: "mg_1_2" },
    { label: "Multigol 1-3", key: "mg_1_3" }, { label: "Multigol 2-3", key: "mg_2_3" },
    { label: "Multigol 2-4", key: "mg_2_4" }, { label: "Multigol 3+", key: "mg_3_plus" },
  ] : [];
  const bestMg = multigol ? mgRanges.reduce((a, b) => (multigol[a.key] || 0) > (multigol[b.key] || 0) ? a : b, mgRanges[0]) : null;

  return (
    <div className="space-y-4">
      <style>{NEON_STYLES}</style>

      {/* ── TARGET KITANO ─────────────────────────────────────────────── */}
      {(tk.type === "no_bet" || tk.bet) && (
        <PremiumGate
          feature="Target Kitano"
          reason="Il pronostico più sicuro della partita"
          testid="premium-gate-target-kitano"
          forceUnlock={isPremium}
        >
          {tk.type === "no_bet" ? (
            <div className="relative overflow-hidden"
              style={{ background: "var(--bc-panel, #100808)", border: "2px solid #EF4444",
                boxShadow: "0 0 24px rgba(239,68,68,0.35), 0 0 60px rgba(239,68,68,0.10)" }}
              data-testid="target-kitano-nobet">
              <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid rgba(239,68,68,0.2)", position: "relative", zIndex: 1 }}>
                <div className="flex items-center gap-2.5">
                  <XCircle size={16} style={{ color: "#EF4444" }} />
                  <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: "#EF4444" }}>TARGET KITANO</span>
                </div>
                <span className="font-data text-xs uppercase px-2 py-0.5"
                  style={{ background: "rgba(239,68,68,0.12)", color: "#EF4444", border: "1px solid rgba(239,68,68,0.3)" }}>
                  SEGNALE DEBOLE
                </span>
              </div>
              <div className="px-4 py-5" style={{ position: "relative", zIndex: 1 }}>
                <p className="font-heading font-bold text-xl uppercase" style={{ color: "#EF4444" }}>NESSUNA SCOMMESSA CONSIGLIATA</p>
                <p className="font-data text-sm mt-2 leading-relaxed" style={{ color: C.muted }}>{tk.reason || "I 3 modelli statistici non convergono su un esito comune."}</p>
                <div className="flex items-center gap-2 mt-3">
                  <span className="font-data text-xs" style={{ color: C.muted }}>Score convergenza:</span>
                  <span className="font-data text-xs font-bold" style={{ color: "#EF4444" }}>{tk.convergence_score}/100</span>
                </div>
              </div>
            </div>
          ) : (
            <KitanoCard tk={tk} accent={tk.has_value ? "value" : "default"}>
              {/* corner accents */}
              <div style={{ position: "absolute", top: 0, left: 0, width: 18, height: 18,
                borderTop: "2px solid #FCD34D", borderLeft: "2px solid #FCD34D", zIndex: 2 }} />
              <div style={{ position: "absolute", top: 0, right: 0, width: 18, height: 18,
                borderTop: "2px solid #FCD34D", borderRight: "2px solid #FCD34D", zIndex: 2 }} />
              <div style={{ position: "absolute", bottom: 0, left: 0, width: 18, height: 18,
                borderBottom: "2px solid #FCD34D", borderLeft: "2px solid #FCD34D", zIndex: 2 }} />
              <div style={{ position: "absolute", bottom: 0, right: 0, width: 18, height: 18,
                borderBottom: "2px solid #FCD34D", borderRight: "2px solid #FCD34D", zIndex: 2 }} />

              <div className="flex items-center justify-between px-4 py-3"
                style={{ borderBottom: "1px solid rgba(245,158,11,0.25)", position: "relative", zIndex: 1 }}>
                <div className="flex items-center gap-2.5">
                  <Zap size={16} style={{ color: "#FCD34D", filter: "drop-shadow(0 0 6px #F59E0B)" }} fill="#FCD34D" />
                  <span className="font-heading font-bold text-sm uppercase tracking-widest"
                    style={{ color: "#FCD34D", textShadow: "0 0 12px rgba(245,158,11,0.8)" }}>
                    TARGET KITANO
                  </span>
                </div>
                <span className="font-data text-xs uppercase px-2 py-0.5"
                  style={{ background: "rgba(245,158,11,0.15)", color: "#FCD34D",
                    border: "1px solid rgba(245,158,11,0.5)", textShadow: "0 0 8px rgba(245,158,11,0.6)" }}>
                  IL PIÙ SICURO
                </span>
              </div>

              <div className="px-4 py-5 flex items-center justify-between gap-4" style={{ position: "relative", zIndex: 1 }}>
                <div className="flex-1">
                  <p className="font-heading font-bold text-2xl uppercase tracking-tight leading-tight"
                    style={{ color: "var(--bc-ink, #FAFAFA)", textShadow: "0 0 18px rgba(245,158,11,0.55), 0 0 35px rgba(245,158,11,0.25)" }}>
                    {tk.bet}
                  </p>
                  <p className="font-data text-xs mt-2 uppercase tracking-wider" style={{ color: "rgba(245,158,11,0.6)" }}>
                    {tk.type === "doppia" ? "Doppia Chance" : tk.type === "gol" ? "Mercato Gol"
                      : tk.type === "multigol" ? "Multigol" : tk.type === "1x2" ? "Esito 1X2" : tk.type}
                  </p>
                  {tk.book_odds && (
                    <p className="font-data text-xs mt-1.5 flex items-center gap-1.5">
                      <span className="uppercase tracking-wide" style={{ color: "rgba(245,158,11,0.5)" }}>Quota Book:</span>
                      <span className="font-bold tabular-nums" style={{ color: "#FCD34D" }}>{tk.book_odds.toFixed(2)}</span>
                    </p>
                  )}
                  {tk.tag && (
                    <div style={{ marginTop: "0.55rem" }}>
                      <BetTag tag={tk.tag} size="md" />
                    </div>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-data font-black tabular-nums"
                    style={{ fontSize: "3.5rem", lineHeight: 1, color: "#F59E0B",
                      textShadow: "0 0 20px rgba(245,158,11,0.9), 0 0 50px rgba(245,158,11,0.4)" }}>
                    {tk.prob}%
                  </p>
                  <p className="font-data text-xs mt-1" style={{ color: "rgba(245,158,11,0.6)" }}>probabilità</p>
                </div>
              </div>
              {onAddBet && isPremium && (
                <div className="px-4 pb-4" style={{ position: "relative", zIndex: 1 }}>
                  <BetBtn bet={tk.bet} prob={tk.prob} type={tk.type} isSelected={isSelected} onAddBet={onAddBet} />
                </div>
              )}
            </KitanoCard>
          )}
        </PremiumGate>
      )}

      {/* ── HYPER COMBO (doppio) ───────────────────────────────────────── */}
      {hc.combo && hc.combo !== "N/D" && hc.prob > 0 && (() => {
        // Pick the combo with higher probability
        const useB = hc.combo_multigol && hc.combo_multigol !== "N/D" && (hc.prob_multigol || 0) > hc.prob;
        const label = useB ? hc.combo_multigol : hc.combo;
        const prob  = useB ? hc.prob_multigol  : hc.prob;
        const isMultigol = useB;
        return (
          <PremiumGate
            feature="Hyper Combo"
            reason="Combinazione esito + mercato gol ad alta probabilità"
            testid="premium-gate-hyper-combo"
            forceUnlock={isPremium}
          >
            <div data-testid="hyper-combo">
            <div className="flex items-center justify-between px-4 py-2.5"
              style={{ background: "var(--bc-panel, #0a0812)", border: "1px solid rgba(167,139,250,0.3)", borderBottom: "none" }}>
              <div className="flex items-center gap-2">
                <Flame size={14} style={{ color: "#A78BFA", filter: "drop-shadow(0 0 5px #A78BFA)" }} fill="#A78BFA" />
                <span className="font-heading font-bold text-sm uppercase tracking-widest"
                  style={{ color: "#A78BFA", textShadow: "0 0 10px rgba(167,139,250,0.7)" }}>
                  HYPER COMBO
                </span>
              </div>
              <span className="font-data text-xs uppercase px-2 py-0.5"
                style={{ background: "rgba(167,139,250,0.12)", color: "#A78BFA", border: "1px solid rgba(167,139,250,0.35)" }}>
                {isMultigol ? "DOPPIA + MULTIGOL" : "ESITO + MERCATO GOL"}
              </span>
            </div>

            <div className={isMultigol ? "neon-cyan hyper-combo-panel" : "neon-purple hyper-combo-panel"} style={{
              background: isMultigol
                ? "linear-gradient(135deg, #050f12 0%, #0a1a1f 100%)"
                : "linear-gradient(135deg, #0d0b18 0%, #12101f 100%)",
              border: isMultigol ? "1.5px solid rgba(6,182,212,0.55)" : "1.5px solid rgba(167,139,250,0.55)",
              position: "relative", overflow: "hidden",
            }}>
              {/* corner accents */}
              {[["top","left"],["bottom","right"]].map(([v,h]) => (
                <div key={v+h} style={{ position:"absolute", [v]:6, [h]:6, width:10, height:10,
                  [`border${v.charAt(0).toUpperCase()+v.slice(1)}`]: `1.5px solid ${isMultigol?"#22D3EE":"#A78BFA"}`,
                  [`border${h.charAt(0).toUpperCase()+h.slice(1)}`]: `1.5px solid ${isMultigol?"#22D3EE":"#A78BFA"}`,
                  zIndex:2 }} />
              ))}
              <div className="px-4 py-4 flex items-center justify-between gap-4" style={{ position:"relative", zIndex:1 }}>
                <p className="font-heading font-bold text-xl uppercase leading-snug flex-1"
                  style={{ color: "var(--bc-ink, #FAFAFA)",
                    textShadow: isMultigol ? "0 0 14px rgba(6,182,212,0.5)" : "0 0 14px rgba(167,139,250,0.5)" }}>
                  {label}
                </p>
                <p className="font-data font-black tabular-nums flex-shrink-0"
                  style={{ fontSize:"3rem", lineHeight:1,
                    color: isMultigol ? "#22D3EE" : "#A78BFA",
                    textShadow: isMultigol
                      ? "0 0 18px rgba(6,182,212,0.85), 0 0 40px rgba(6,182,212,0.35)"
                      : "0 0 18px rgba(167,139,250,0.85), 0 0 40px rgba(167,139,250,0.35)" }}>
                  {prob}%
                </p>
              </div>
              {onAddBet && isPremium && (
                <div className="px-4 pb-4" style={{ position:"relative", zIndex:1 }}>
                  <BetBtn bet={label} prob={prob} type="combo" isSelected={isSelected} onAddBet={onAddBet} />
                </div>
              )}
            </div>
          </div>
          </PremiumGate>
        );
      })()}

      {/* 1X2 */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="prediction-1x2">
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="flex items-center gap-2">
            <Zap size={14} style={{ color: C.green }} />
            <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: C.text }}>PRONOSTICO 1X2</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-data text-xs" style={{ color: C.muted }}>Fiducia:</span>
            <span className="font-data text-sm font-medium" style={{ color: C.green }}>{confidence}%</span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-px" style={{ background: C.border }}>
          {[{ key: "home", label: homeTeam, prob: combined.home, color: C.green },
            { key: "draw", label: "Pareggio", prob: combined.draw, color: C.muted },
            { key: "away", label: awayTeam, prob: combined.away, color: C.red }
          ].map(({ key, label, prob, color }) => (
            <div key={key} className="flex flex-col items-center py-5 px-3 relative"
              style={{ background: dominant === key ? `${color}0d` : C.surface }} data-testid={`prob-${key}`}>
              {dominant === key && <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: color }} />}
              <span className="font-data font-medium text-4xl" style={{ color }}>{prob}%</span>
              <span className="font-heading text-xs uppercase tracking-wider mt-1 text-center truncate w-full" style={{ color: C.muted }}>
                {key === "draw" ? "PAREGGIO" : label}
              </span>
              {dominant === key && <Star size={10} className="mt-1" style={{ color }} fill={color} />}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 px-4 py-3" style={{ borderTop: `1px solid ${C.border}` }}>
          <div className="text-center">
            <p className="font-data text-lg font-medium" style={{ color: C.green }}>{combined.home_xg}</p>
            <p className="font-data text-xs" style={{ color: C.muted }}>xG CASA</p>
          </div>
          <div className="text-center">
            <p className="font-heading text-sm uppercase" style={{ color: C.muted }}>GOAL ATTESI</p>
            <p className="font-data text-xs mt-1" style={{ color: C.muted }}>{combined.most_likely_score}</p>
          </div>
          <div className="text-center">
            <p className="font-data text-lg font-medium" style={{ color: C.red }}>{combined.away_xg}</p>
            <p className="font-data text-xs" style={{ color: C.muted }}>xG OSPITE</p>
          </div>
        </div>
        {onAddBet && (
          <div className="flex flex-wrap gap-1.5 px-4 py-2.5" style={{ borderTop: `1px solid ${C.highlight}` }}>
            <BetBtn bet={`1 (${homeTeam})`} prob={combined.home} type="1x2" isSelected={isSelected} onAddBet={onAddBet} />
            <BetBtn bet="X (Pareggio)" prob={combined.draw} type="1x2" isSelected={isSelected} onAddBet={onAddBet} />
            <BetBtn bet={`2 (${awayTeam})`} prob={combined.away} type="1x2" isSelected={isSelected} onAddBet={onAddBet} />
          </div>
        )}
      </div>

      {/* Goals Predictions */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="goals-predictions">
        <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
          <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: C.text }}>PRONOSTICI GOL</span>
        </div>
        <div className="px-4 py-3 space-y-0">
          <GoalRow label="Over 1.5" prob={combined.over15} color={C.green} />
          <GoalRow label="Under 1.5" prob={combined.under15 ?? (100 - combined.over15)} color={C.blue} />
          <GoalRow label="Over 2.5" prob={combined.over25} color={C.green} />
          <GoalRow label="Under 2.5" prob={combined.under25} color={C.blue} />
          <GoalRow label="Over 3.5" prob={combined.over35} color={C.yellow} />
          <GoalRow label="Under 3.5" prob={combined.under35 ?? (100 - combined.over35)} color={C.blue} />
          <GoalRow label="Gol/Gol" prob={combined.btts_yes} color={C.green} />
          <GoalRow label="No Gol/Gol" prob={combined.btts_no} color={C.red} />
        </div>
        {onAddBet && (
          <div className="flex flex-wrap gap-1.5 px-4 py-2.5" style={{ borderTop: `1px solid ${C.highlight}` }}>
            <BetBtn bet="Over 1.5" prob={combined.over15} type="gol" isSelected={isSelected} onAddBet={onAddBet} />
            <BetBtn bet="Over 2.5" prob={combined.over25} type="gol" isSelected={isSelected} onAddBet={onAddBet} />
            <BetBtn bet="Under 1.5" prob={combined.under15 ?? (100 - combined.over15)} type="gol" isSelected={isSelected} onAddBet={onAddBet} />
            <BetBtn bet="Under 2.5" prob={combined.under25} type="gol" isSelected={isSelected} onAddBet={onAddBet} />
            <BetBtn bet="Under 3.5" prob={combined.under35 ?? (100 - combined.over35)} type="gol" isSelected={isSelected} onAddBet={onAddBet} />
            <BetBtn bet="Gol/Gol (BTTS)" prob={combined.btts_yes} type="gol" isSelected={isSelected} onAddBet={onAddBet} />
            <BetBtn bet="No Gol/Gol" prob={combined.btts_no} type="gol" isSelected={isSelected} onAddBet={onAddBet} />
          </div>
        )}
      </div>

      {/* Multigol */}
      {multigol && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="multigol-panel">
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
            <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: C.text }}>MULTIGOL TOTALE</span>
          </div>
          <div className="px-4 py-3">
            {mgRanges.map(({ label, key }) => (
              <MulrigolRow key={key} label={label} prob={multigol[key] || 0} highlight={bestMg?.key === key} />
            ))}
          </div>
          <div className="grid grid-cols-2 gap-px" style={{ borderTop: `1px solid ${C.border}`, background: C.border }}>
            <div className="px-4 py-3" style={{ background: C.surface }}>
              <p className="font-data text-xs uppercase tracking-wider mb-2" style={{ color: C.green }}>GOL CASA</p>
              {[["0 gol", "home_0"], ["1 gol", "home_1"], ["2 gol", "home_2"], ["1+ gol", "home_1_plus"], ["2+ gol", "home_2_plus"]].map(([l, k]) => (
                <div key={k} className="flex justify-between py-1" style={{ borderBottom: `1px solid ${C.highlight}` }}>
                  <span className="font-data text-xs" style={{ color: C.muted }}>{l}</span>
                  <span className="font-data text-xs font-medium" style={{ color: C.text }}>{multigol[k] || 0}%</span>
                </div>
              ))}
            </div>
            <div className="px-4 py-3" style={{ background: C.surface }}>
              <p className="font-data text-xs uppercase tracking-wider mb-2" style={{ color: C.red }}>GOL OSPITE</p>
              {[["0 gol", "away_0"], ["1 gol", "away_1"], ["2 gol", "away_2"], ["1+ gol", "away_1_plus"], ["2+ gol", "away_2_plus"]].map(([l, k]) => (
                <div key={k} className="flex justify-between py-1" style={{ borderBottom: `1px solid ${C.highlight}` }}>
                  <span className="font-data text-xs" style={{ color: C.muted }}>{l}</span>
                  <span className="font-data text-xs font-medium" style={{ color: C.text }}>{multigol[k] || 0}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Multigol Bet Buttons */}
      {multigol && onAddBet && (
        <div className="flex flex-wrap gap-1.5 px-4 py-2.5" style={{ background: C.surface, border: `1px solid ${C.border}`, borderTop: "none" }}>
          <BetBtn bet="Multigol 0-1" prob={multigol.mg_0_1 || 0} type="multigol" isSelected={isSelected} onAddBet={onAddBet} />
          <BetBtn bet="Multigol 1-2" prob={multigol.mg_1_2 || 0} type="multigol" isSelected={isSelected} onAddBet={onAddBet} />
          <BetBtn bet="Multigol 1-3" prob={multigol.mg_1_3 || 0} type="multigol" isSelected={isSelected} onAddBet={onAddBet} />
          <BetBtn bet="Multigol 2-3" prob={multigol.mg_2_3 || 0} type="multigol" isSelected={isSelected} onAddBet={onAddBet} />
          <BetBtn bet="Multigol 2-4" prob={multigol.mg_2_4 || 0} type="multigol" isSelected={isSelected} onAddBet={onAddBet} />
        </div>
      )}

      {/* Double Chance */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="double-chance">
        <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
          <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: C.text }}>DOPPIA CHANCE</span>
        </div>
        <div className="px-4 py-4 space-y-3">
          <ProbBar label="1X (Casa o Pareggio)" prob={combined.double_1x} color={C.green} />
          <ProbBar label="X2 (Ospite o Pareggio)" prob={combined.double_x2} color={C.blue} />
          <ProbBar label="12 (Casa o Ospite)" prob={combined.double_12} color={C.yellow} />
        </div>
        {onAddBet && (
          <div className="flex flex-wrap gap-1.5 px-4 py-2.5" style={{ borderTop: `1px solid ${C.highlight}` }}>
            <BetBtn bet="1X (Casa o Pareggio)" prob={combined.double_1x} type="doppia" isSelected={isSelected} onAddBet={onAddBet} />
            <BetBtn bet="X2 (Ospite o Pareggio)" prob={combined.double_x2} type="doppia" isSelected={isSelected} onAddBet={onAddBet} />
            <BetBtn bet="12 (Casa o Ospite)" prob={combined.double_12} type="doppia" isSelected={isSelected} onAddBet={onAddBet} />
          </div>
        )}
      </div>

      {/* AFFIDABILITÀ + Recommended Bets */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* Affidabilità */}
        {(() => {
          const score = aff.score ?? 0;
          const level = aff.level ?? "MEDIA";
          const color = level === "ALTA" ? C.green : level === "MEDIA" ? C.yellow : C.red;
          const bgColor = level === "ALTA" ? "rgba(34,197,94,0.08)" : level === "MEDIA" ? "rgba(245,158,11,0.08)" : "rgba(239,68,68,0.08)";
          const borderColor = level === "ALTA" ? "rgba(34,197,94,0.3)" : level === "MEDIA" ? "rgba(245,158,11,0.3)" : "rgba(239,68,68,0.3)";
          const Icon = level === "ALTA" ? CheckCircle : level === "MEDIA" ? Minus : XCircle;
          const comps = aff.components || {};
          return (
            <div style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="affidabilita-panel">
              <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.border}` }}>
                <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: C.text }}>AFFIDABILITÀ</span>
                <div className="flex items-center gap-2">
                  <span className="font-data text-xs uppercase px-2 py-0.5"
                    style={{ background: bgColor, color, border: `1px solid ${borderColor}` }}>
                    {level}
                  </span>
                  <button
                    data-testid="affidabilita-help-btn"
                    onClick={() => setShowAffHelp(v => !v)}
                    className="flex items-center justify-center w-6 h-6 transition-all"
                    style={{
                      background: showAffHelp ? "rgba(167,139,250,0.15)" : "var(--bc-border, rgba(255,255,255,0.07))",
                      border: `1px solid ${showAffHelp ? "rgba(167,139,250,0.4)" : "var(--bc-border, rgba(255,255,255,0.1))"}`,
                      color: showAffHelp ? "#A78BFA" : C.muted,
                    }}
                    title="Cosa significa questo punteggio?">
                    {showAffHelp ? <X size={12} /> : <HelpCircle size={12} />}
                  </button>
                </div>
              </div>

              {/* Spiegazione inline — visibile solo quando showAffHelp = true */}
              {showAffHelp && (
                <div data-testid="affidabilita-help-content"
                  style={{
                    background: "rgba(167,139,250,0.05)",
                    borderBottom: `1px solid rgba(167,139,250,0.15)`,
                  }}
                  className="px-4 py-4 space-y-3">
                  <p className="font-heading font-bold text-xs uppercase tracking-widest mb-3" style={{ color: "#A78BFA" }}>
                    Come si calcola l'Affidabilità?
                  </p>
                  {[
                    {
                      icon: "⚡",
                      title: "Convergenza dei modelli (40%)",
                      desc: "Tre algoritmi indipendenti (Poisson, ELO e Forma recente) analizzano la partita. Se concordano tutti sullo stesso esito, il punteggio sale. Se divergono, scende.",
                      val: comps.convergence,
                    },
                    {
                      icon: "🎯",
                      title: "Dominio dell'esito (35%)",
                      desc: "Misura quanto un esito (vittoria casa, pareggio o vittoria ospite) sia chiaramente più probabile degli altri. Una partita con 70% a favore di una squadra è molto più chiara di una 36-32-32.",
                      val: comps.dominance,
                    },
                    {
                      icon: "🔐",
                      title: "Qualità del pronostico TK (25%)",
                      desc: "Valuta la probabilità del pronostico Target Kitano. Se il modello individua una giocata con alta probabilità (es. Over 1.5 all'84%), il punteggio aumenta. Se consiglia 'NO BET', scende a zero.",
                      val: comps.tk_quality,
                    },
                  ].map(({ icon, title, desc, val }) => (
                    <div key={title} className="flex gap-3">
                      <span className="text-base flex-shrink-0 mt-0.5">{icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-heading font-bold text-xs uppercase tracking-wide" style={{ color: C.text }}>{title}</span>
                          {val !== undefined && (
                            <span className="font-data text-xs tabular-nums" style={{ color: C.muted }}>{val}/100</span>
                          )}
                        </div>
                        <p className="font-data text-xs leading-relaxed" style={{ color: C.muted }}>{desc}</p>
                      </div>
                    </div>
                  ))}
                  <div className="pt-2" style={{ borderTop: `1px solid rgba(167,139,250,0.12)` }}>
                    <p className="font-data text-xs leading-relaxed" style={{ color: C.muted }}>
                      <strong style={{ color: C.text }}>Come leggerlo:</strong>{" "}
                      <span style={{ color: C.green }}>ALTA (≥62)</span> — giocata ben supportata.{" "}
                      <span style={{ color: C.yellow }}>MEDIA (38–61)</span> — scommetti con cautela.{" "}
                      <span style={{ color: C.red }}>BASSA (&lt;38)</span> — evita questa partita.
                    </p>
                  </div>
                </div>
              )}

              <div className="px-4 py-4 space-y-4">
                {/* Main score */}
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 flex items-center justify-center flex-shrink-0"
                    style={{ background: bgColor, border: `2px solid ${color}` }}>
                    <Icon size={22} style={{ color }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-data font-bold text-3xl tabular-nums" style={{ color }}>{score}</span>
                      <span className="font-data text-sm" style={{ color: C.muted }}>/100</span>
                    </div>
                    <div className="mt-1.5 h-2.5 overflow-hidden relative"
                      style={{ background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))", borderRadius: 4, border: "1px solid var(--bc-border, rgba(255,255,255,0.07))" }}>
                      <div
                        className="h-full relative overflow-hidden"
                        style={{
                          width: `${score}%`,
                          background: `linear-gradient(to right, ${color}35, ${color}80, ${color})`,
                          boxShadow: `0 0 12px ${color}70`,
                          borderRadius: "3px 0 0 3px",
                        }}
                      >
                        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "42%",
                          background: "linear-gradient(to bottom, rgba(255,255,255,0.22), transparent)" }} />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Description */}
                <p className="font-data text-xs leading-relaxed" style={{ color: C.muted }}>
                  {aff.description || "Valutazione composita su convergenza modelli, dominio dell'esito e qualità del pronostico."}
                </p>
                {/* Sub-components */}
                {comps.convergence !== undefined && (
                  <div className="space-y-2.5 pt-1" style={{ borderTop: `1px solid ${C.highlight}` }}>
                    {[
                      { label: "Convergenza modelli", val: comps.convergence },
                      { label: "Dominio esito", val: comps.dominance },
                      { label: "Qualità TK", val: comps.tk_quality },
                    ].map(({ label, val }) => (
                      <div key={label} className="flex items-center gap-2">
                        <span className="font-data text-xs w-32 flex-shrink-0" style={{ color: C.muted }}>{label}</span>
                        <div
                          className="flex-1 h-1.5 overflow-hidden relative"
                          style={{ background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))", borderRadius: 3, border: "1px solid var(--bc-border, rgba(255,255,255,0.07))" }}
                        >
                          <div
                            className="h-full relative overflow-hidden"
                            style={{
                              width: `${val}%`,
                              background: `linear-gradient(to right, ${color}30, ${color}70, ${color})`,
                              boxShadow: `0 0 6px ${color}50`,
                              borderRadius: "2px 0 0 2px",
                              opacity: 0.75,
                            }}
                          >
                            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "42%",
                              background: "linear-gradient(to bottom, rgba(255,255,255,0.18), transparent)" }} />
                          </div>
                        </div>
                        <span className="font-data text-xs w-8 text-right tabular-nums" style={{ color: C.muted }}>{val}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Scommesse Consigliate */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="recommended-bets">
          <div className="px-4 py-3 flex items-center justify-between gap-3" style={{ borderBottom: `1px solid ${C.border}` }}>
            <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: C.text }}>SCOMMESSE CONSIGLIATE</span>
            <BetTagsLegend />
          </div>
          <div className="px-4 py-3 space-y-3">
            {(() => {
              // Free users see only the 1st bet; the rest get blurred + CTA.
              const visibleRows = isPremium ? recommended_bets : recommended_bets.slice(0, 1);
              const hiddenRows = isPremium ? [] : recommended_bets.slice(1);
              const renderRow = (bet, i, total) => {
                if (bet.type === "no_bet") {
                  return (
                    <div key={`nobet-${i}`} className="flex items-center gap-3 py-2">
                      <XCircle size={14} style={{ color: C.red, flexShrink: 0 }} />
                      <span className="flex-1 font-heading font-bold text-xs uppercase" style={{ color: C.red }}>NO BET — {bet.reason || "Modelli divergenti"}</span>
                    </div>
                  );
                }
                const BetIcon = BET_ICON[bet.type] || Minus;
                const betColor = bet.prob > 65 ? C.green : bet.prob > 50 ? C.yellow : C.muted;
                return (
                  <div key={bet.bet || i} className="flex items-center gap-3 py-2"
                    style={{ borderBottom: i < total - 1 ? `1px solid ${C.highlight}` : "none" }}>
                    <BetIcon size={14} style={{ color: betColor, flexShrink: 0 }} />
                    <span className="flex-1 font-heading font-bold text-xs uppercase" style={{ color: C.text }}>{bet.bet}</span>
                    {bet.tag && <BetTag tag={bet.tag} size="sm" showEdge={false} />}
                    {bet.book_odds && (
                      <span className="font-data text-xs tabular-nums font-bold" style={{ color: C.cyan }}>{bet.book_odds.toFixed(2)}</span>
                    )}
                    {bet.prob > 0 && <span className="font-data text-sm font-medium" style={{ color: betColor }}>{bet.prob}%</span>}
                  </div>
                );
              };
              return (
                <>
                  {visibleRows.map((bet, i) => renderRow(bet, i, visibleRows.length + (hiddenRows.length ? 1 : 0)))}
                  {hiddenRows.length > 0 && (
                    <div
                      data-testid="recommended-bets-locked"
                      onClick={() => { window.location.href = "/pricing"; }}
                      style={{
                        position: "relative",
                        marginTop: 6,
                        cursor: "pointer",
                        padding: "0.6rem 0.85rem",
                        background: "rgba(200,255,0,0.04)",
                        border: "1px dashed rgba(200,255,0,0.55)",
                        color: "#C8FF00",
                        fontFamily: "'Barlow Condensed',sans-serif",
                        fontWeight: 800,
                        fontSize: "0.78rem",
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        boxShadow: "0 0 14px rgba(200,255,0,0.25)",
                      }}
                    >
                      <span>🔒 +{hiddenRows.length} {hiddenRows.length === 1 ? "scommessa" : "scommesse"} Premium</span>
                      <span style={{
                        fontFamily: "'JetBrains Mono',monospace",
                        fontSize: "0.62rem", letterSpacing: "0.12em",
                        color: "#FFFFFF",
                      }}>
                        Sblocca →
                      </span>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ─── VALUEBET STRIP — appears only when at least one value bet
           passes both the edge ≥ 8 % and confidence-floor gates. Shows
           the underlying numbers (model vs Pinnacle/market implied) and
           a short natural-language rationale per pick. */}
      {valueBets.length > 0 && isPremium && (
        <ValuebetStrip
          valueBets={valueBets}
          C={C}
          isSelected={isSelected}
          onAddBet={onAddBet}
        />
      )}
    </div>
  );
}

/* ─── ValuebetStrip — neon-green panel that surfaces VALUE / PREMIUM
   bets (positive de-vigged edge ≥ 5 %, prob ≥ 50 %, affidabilità ≥ 50).
   Auto-collapsed by default; expands to show why each pick made the cut. */

function ValuebetStrip({ valueBets, C, isSelected, onAddBet }) {
  const G = "#22C55E";
  const [open, setOpen] = useState(false);
  const premiumCount = valueBets.filter(([, v]) => (v.value_pct || 0) >= 12).length;
  return (
    <div
      data-testid="valuebet-strip"
      className="relative overflow-hidden valuebet-strip"
      style={{
        background: "linear-gradient(135deg, #04140a 0%, #07210f 60%, #04140a 100%)",
        border: `1.5px solid ${G}`,
        boxShadow: `0 0 24px rgba(34,197,94,0.32), 0 0 60px rgba(34,197,94,0.12)`,
      }}
    >
      {/* corner accents */}
      {[["top","left"],["top","right"],["bottom","left"],["bottom","right"]].map(([v,h]) => (
        <div key={v+h} style={{ position:"absolute", [v]:0, [h]:0, width:14, height:14,
          [`border${v.charAt(0).toUpperCase()+v.slice(1)}`]: `2px solid ${G}`,
          [`border${h.charAt(0).toUpperCase()+h.slice(1)}`]: `2px solid ${G}`, zIndex: 2 }} />
      ))}

      {/* Header — clickable toggle */}
      <button
        type="button"
        data-testid="valuebet-strip-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 transition-colors"
        style={{
          borderBottom: open ? `1px solid rgba(34,197,94,0.25)` : "none",
          position: "relative", zIndex: 1, cursor: "pointer",
          background: "transparent",
        }}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <TrendingUp size={15} style={{ color: G, filter: `drop-shadow(0 0 6px ${G})` }} />
          <span
            className="font-heading font-bold text-sm uppercase tracking-widest"
            style={{ color: G, textShadow: `0 0 12px rgba(34,197,94,0.7)` }}
          >
            VALUE BET DEL MATCH
          </span>
          <span
            className="font-data text-xs px-1.5 py-0.5 ml-1"
            style={{ background: "rgba(34,197,94,0.18)", color: G,
              border: `1px solid rgba(34,197,94,0.45)` }}
          >
            {valueBets.length}
          </span>
          {premiumCount > 0 && (
            <span
              className="font-data text-xs px-1.5 py-0.5"
              title={`${premiumCount} pick con edge eccezionale (≥ 12%)`}
              style={{ background: "rgba(34,211,238,0.15)", color: "#22D3EE",
                border: `1px solid rgba(34,211,238,0.5)` }}
            >
              💎 {premiumCount} PREMIUM
            </span>
          )}
        </div>
        <ChevronDown
          size={18}
          style={{
            color: G,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.25s ease",
            filter: `drop-shadow(0 0 4px ${G})`,
          }}
        />
      </button>

      {open && (
        <div className="px-4 py-3" style={{ position: "relative", zIndex: 1 }}>
          {/* Plain-Italian explainer — replaces the old jargon copy */}
          <div className="mb-3 px-3 py-2.5"
            style={{ background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.22)" }}>
            <p className="font-data text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
              <span style={{ color: G, fontWeight: 700 }}>Cosa significa.</span>{" "}
              Le scommesse qui sotto hanno un <b>edge positivo</b>: secondo il nostro modello, la probabilità reale è più alta di quella implicita nella quota del bookmaker (sgrassata dal margine). In pratica: <i>il book sta pagando più del giusto</i>.
            </p>
            <p className="font-data text-[11px] mt-1.5" style={{ color: "rgba(255,255,255,0.55)" }}>
              Filtri attivi: edge ≥ 5% · probabilità ≥ 50% · affidabilità ≥ 50/100. Riferimento: Pinnacle de-viggato (modello Shin) o media mercato.
            </p>
          </div>

          <div className="space-y-2.5">
            {valueBets.map(([key, v]) => {
              const label = v._label || key;
              const type = v._type || "value";
              const odds = v.odds ? Number(v.odds).toFixed(2) : "—";
              const edge = v.value_pct != null ? Number(v.value_pct).toFixed(1) : "—";
              const modelP = v.model_prob != null ? Math.round(Number(v.model_prob)) : null;
              const impliedP = v.implied_prob != null ? Math.round(Number(v.implied_prob)) : null;
              const sourceLbl = v.source === "pinnacle" ? "Pinnacle" : "Mercato";
              const isPremium = Number(v.value_pct || 0) >= 12;
              const tagColor = isPremium ? "#22D3EE" : G;
              const tagIcon = isPremium ? "💎" : "🎯";
              const tagLabel = isPremium ? "PREMIUM" : "VALUE";
              return (
                <div
                  key={key}
                  data-testid={`valuebet-row-${key}`}
                  className="relative"
                  style={{
                    background: `${tagColor}0D`,
                    border: `1px solid ${tagColor}55`,
                    padding: "10px 12px",
                  }}
                >
                  <div className="flex items-center gap-3">
                    <span
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "0.18rem 0.4rem",
                        background: `${tagColor}1F`,
                        border: `1px solid ${tagColor}`,
                        color: tagColor,
                        fontFamily: "'Barlow Condensed',sans-serif",
                        fontSize: "0.6rem", fontWeight: 800,
                        letterSpacing: "0.16em",
                      }}
                    >
                      <span aria-hidden>{tagIcon}</span>{tagLabel}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-bold text-sm uppercase tracking-wide truncate" style={{ color: C.text }}>
                        {label}
                      </p>
                      <p className="font-data text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.55)" }}>
                        Modello dà <b style={{ color: C.text }}>{modelP}%</b>
                        {impliedP != null ? <> · {sourceLbl} (de-vig) <b style={{ color: C.text }}>{impliedP}%</b></> : null}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-data font-bold tabular-nums" style={{ color: tagColor, fontSize: "1.45rem", lineHeight: 1, textShadow: `0 0 10px ${tagColor}80` }}>
                        {odds}
                      </p>
                      <p className="font-data text-[10px] uppercase mt-0.5" style={{ color: "rgba(255,255,255,0.5)" }}>
                        quota {sourceLbl.toLowerCase()}
                      </p>
                    </div>
                    <div
                      className="flex-shrink-0 px-2 py-1 font-data text-xs font-bold tabular-nums"
                      title="Edge: di quanto la quota paga MEGLIO del giusto secondo il modello"
                      style={{ background: `${tagColor}2E`, color: tagColor, border: `1px solid ${tagColor}`, minWidth: 64, textAlign: "center" }}
                    >
                      +{edge}%
                    </div>
                  </div>
                  {onAddBet && (
                    <div className="mt-2 flex">
                      <BetBtn
                        bet={label}
                        prob={modelP || 0}
                        type={type}
                        isSelected={isSelected}
                        onAddBet={onAddBet}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
